import { useRef, useEffect } from "react";

export default function WavingFlag({ src, width = 80, height = 50, speed = 0.8 }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    imgRef.current = img;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    img.onload = () => {
      const animate = () => {
        timeRef.current += 0.02 * speed;
        drawFlag(ctx, img, width, height, timeRef.current);
        animRef.current = requestAnimationFrame(animate);
      };
      animate();
    };

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [src, width, height, speed]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, display: "block" }}
      data-testid="waving-flag-canvas"
    />
  );
}

function drawFlag(ctx, img, w, h, t) {
  ctx.clearRect(0, 0, w, h);

  const cols = 40;
  const sliceW = img.width / cols;
  const drawSliceW = w / cols + 0.5; // slight overlap to avoid gaps

  for (let i = 0; i < cols; i++) {
    const xRatio = i / cols;

    // Wave amplitude increases from hoist (left) to fly (right)
    const amp = xRatio * xRatio * 6;

    // Multiple wave frequencies for realistic cloth feel
    const wave1 = Math.sin(t * 3.0 - xRatio * 8) * amp;
    const wave2 = Math.sin(t * 2.2 - xRatio * 5 + 1.2) * amp * 0.4;
    const wave3 = Math.sin(t * 4.5 - xRatio * 12 + 2.5) * amp * 0.15;
    const yOff = wave1 + wave2 + wave3;

    // Slight horizontal compression/stretch for depth
    const xWave = Math.sin(t * 2.5 - xRatio * 7) * xRatio * 2;

    // Scale variation for 3D perspective (billowing effect)
    const scaleY = 1 + Math.sin(t * 2.8 - xRatio * 6) * xRatio * 0.06;

    // Brightness variation for lighting/shadow
    const shade = 0.85 + Math.sin(t * 3.0 - xRatio * 8 + 0.5) * xRatio * 0.15;

    const dx = (i / cols) * w + xWave;
    const dy = yOff;
    const dh = h * scaleY;

    ctx.save();

    // Apply brightness
    ctx.globalAlpha = 1;
    ctx.drawImage(
      img,
      i * sliceW, 0, sliceW + 1, img.height,    // source slice
      dx, (h - dh) / 2 + dy, drawSliceW, dh       // destination
    );

    // Shadow overlay for depth
    if (shade < 0.92) {
      ctx.globalAlpha = (0.92 - shade) * 1.5;
      ctx.fillStyle = "#000";
      ctx.fillRect(dx, (h - dh) / 2 + dy, drawSliceW, dh);
    }
    // Highlight overlay
    if (shade > 0.95) {
      ctx.globalAlpha = (shade - 0.95) * 2;
      ctx.fillStyle = "#fff";
      ctx.fillRect(dx, (h - dh) / 2 + dy, drawSliceW, dh);
    }

    ctx.restore();
  }
}
